/**
 * CoachBioSection — bio + foto + stats + metodología + contacto.
 * Foto y branding (name, tagline) vienen de `brand` (no editables aquí).
 */
import type { CoachBioData, PlanBrand } from "../../../types/plan";
import { InlineEditable } from "../InlineEditable";
import { ImageUploader } from "../../../lib/ImageUploader";
import { PageHeader, SectionPage } from "./section-shared";

interface CoachBioSectionProps {
  data: CoachBioData;
  brand: PlanBrand | null;
  onChange: (patch: Partial<CoachBioData>) => void;
  readonly?: boolean;
}

export function CoachBioSection({ data, brand, onChange, readonly }: CoachBioSectionProps) {
  const updateStat = (i: number, key: "label" | "value", v: string) => {
    const stats = [...(data.stats || [])];
    stats[i] = { ...stats[i], [key]: v };
    onChange({ stats });
  };

  return (
    <SectionPage readonly={readonly}>
      <PageHeader eyebrow="Sobre el entrenador" title="Sobre el entrenador" />
      <div className="al-bio-grid">
        <div className="al-bio-photo">
          {readonly ? (
            data.photo ? (
              <img src={data.photo} alt={brand?.name || "Coach"} />
            ) : (
              <div className="al-bio-photo-fb">
                <b>{brand?.logoLetters || "AL"}</b>
                <span>{brand?.name || ""}</span>
              </div>
            )
          ) : (
            <ImageUploader
              value={data.photo || null}
              onChange={(url) => onChange({ photo: url || null })}
              folder="brand"
              shape="square"
              size={180}
              // MISMA config que Mi marca (que el coach confirma que funciona
              // bien). El usuario reportó que con aspect=3/4 forzado el
              // cropper le mostraba bandas raras en su foto vertical del
              // celular — porque 3:4 es MÁS ANCHO que 9:16. Mejor que el
              // recorte default sea 1:1 (igual de Mi marca), y la tarjeta
              // del plan ahora usa object-fit:contain así que NO recorta
              // de nuevo en el render — solo letterbox arriba/abajo en su
              // tarjeta 3:4 si la foto guardada es cuadrada.
              label="Subir foto del entrenador"
            />
          )}
        </div>
        <div>
          <div className="al-bio-name">{brand?.name || "Entrenador"}</div>
          <div className="al-bio-tag">{(brand?.tagline || "ENTRENADOR PERSONAL").toUpperCase()}</div>
          <p className="al-bio-text">
            <InlineEditable
              value={data.bio}
              onChange={(v) => onChange({ bio: v })}
              multiline
              readonly={readonly}
              placeholder="Bio del entrenador…"
              fieldPath="bio"
            />
          </p>
          <div className="al-bio-stats">
            {(data.stats || []).map((st, i) => (
              <div key={i}>
                <b>
                  <InlineEditable
                    value={st.value}
                    onChange={(v) => updateStat(i, "value", v)}
                    readonly={readonly}
                    fieldPath={`stats.${i}.value`}
                  />
                </b>
                <span>
                  <InlineEditable
                    value={st.label}
                    onChange={(v) => updateStat(i, "label", v)}
                    readonly={readonly}
                    fieldPath={`stats.${i}.label`}
                  />
                </span>
              </div>
            ))}
          </div>
          <div className="al-bio-method">
            <div className="al-pg-eyebrow">METODOLOGÍA</div>
            <InlineEditable
              value={data.methodology}
              onChange={(v) => onChange({ methodology: v })}
              multiline
              readonly={readonly}
              placeholder="Metodología…"
              fieldPath="methodology"
            />
          </div>
          <div className="al-bio-contact">
            <InlineEditable
              value={data.contact || ""}
              onChange={(v) => onChange({ contact: v })}
              readonly={readonly}
              placeholder="Teléfono / email"
              fieldPath="contact"
            />
            {brand?.web ? <> · {brand.web}</> : null}
            {brand?.ig ? <> · {brand.ig}</> : null}
          </div>
        </div>
      </div>
    </SectionPage>
  );
}
